import { useReducer, useCallback } from 'react';

/**
 * Turn-Taking State Machine
 *
 * Manages conversation flow between user and AI
 *
 * States:
 * - IDLE: Waiting for user to speak
 * - USER_SPEAKING: User is talking
 * - PROCESSING: Processing user's speech (STT + AI thinking)
 * - AI_SPEAKING: AI is responding
 *
 * Transitions:
 * IDLE -> USER_SPEAKING (on speech detected)
 * IDLE -> AI_SPEAKING (on proactive greeting/response)
 * USER_SPEAKING -> PROCESSING (on speech end)
 * PROCESSING -> AI_SPEAKING (on AI response ready)
 * PROCESSING -> AI_SPEAKING (on error - to speak error message)
 * PROCESSING -> IDLE (on error fallback via AI_SPEECH_COMPLETE)
 * AI_SPEAKING -> IDLE (on AI speech complete)
 * AI_SPEAKING -> USER_SPEAKING (on user interruption)
 * ANY_STATE -> IDLE (on error recovery after TTS error message)
 */

const STATES = {
  IDLE: 'IDLE',
  USER_SPEAKING: 'USER_SPEAKING',
  PROCESSING: 'PROCESSING',
  AI_SPEAKING: 'AI_SPEAKING'
};

const EVENTS = {
  SPEECH_START: 'SPEECH_START',
  SPEECH_END: 'SPEECH_END',
  START_PROCESSING: 'START_PROCESSING',
  AI_RESPONSE_READY: 'AI_RESPONSE_READY',
  AI_SPEECH_COMPLETE: 'AI_SPEECH_COMPLETE',
  USER_INTERRUPT: 'USER_INTERRUPT',
  ERROR_OCCURRED: 'ERROR_OCCURRED'
};

const initialState = {
  currentState: STATES.IDLE,
  history: [],
  vadEnabled: true,        // VAD is enabled when listening
  micEnabled: true         // Mic is always on
};

function reducer(state, action) {
  const { type, payload } = action;
  const { currentState } = state;

  console.log(`🔄 State transition: ${currentState} --[${type}]--> ?`);

  switch (type) {
    case EVENTS.SPEECH_START:
      if (currentState === STATES.IDLE) {
        return {
          ...state,
          currentState: STATES.USER_SPEAKING,
          history: [...state.history, { from: currentState, to: STATES.USER_SPEAKING, event: type }]
        };
      }
      // If user speaks during AI_SPEAKING, interrupt immediately
      if (currentState === STATES.AI_SPEAKING) {
        return {
          ...state,
          currentState: STATES.USER_SPEAKING,
          vadEnabled: true,
          history: [...state.history, { from: currentState, to: STATES.USER_SPEAKING, event: EVENTS.USER_INTERRUPT }]
        };
      }
      return state;

    case EVENTS.SPEECH_END:
      if (currentState === STATES.USER_SPEAKING) {
        return {
          ...state,
          currentState: STATES.PROCESSING,
          history: [...state.history, { from: currentState, to: STATES.PROCESSING, event: type }]
        };
      }
      return state;

    case EVENTS.AI_RESPONSE_READY:
      // Allow transition from PROCESSING (normal flow) or IDLE (proactive greetings)
      if (currentState === STATES.PROCESSING || currentState === STATES.IDLE) {
        return {
          ...state,
          currentState: STATES.AI_SPEAKING,
          vadEnabled: false,  // Disable VAD during AI speech to prevent echo
          history: [...state.history, { from: currentState, to: STATES.AI_SPEAKING, event: type }]
        };
      }
      return state;

    case EVENTS.AI_SPEECH_COMPLETE:
      // Accept from AI_SPEAKING (normal flow) or PROCESSING (safety fallback for error recovery)
      if (currentState === STATES.AI_SPEAKING || currentState === STATES.PROCESSING) {
        return {
          ...state,
          currentState: STATES.IDLE,
          vadEnabled: true,   // Re-enable VAD after AI finishes
          history: [...state.history, { from: currentState, to: STATES.IDLE, event: type }]
        };
      }
      return state;

    case EVENTS.ERROR_OCCURRED:
      // Universal error recovery - transition to AI_SPEAKING to deliver error message via TTS
      if (currentState === STATES.PROCESSING) {
        return {
          ...state,
          currentState: STATES.AI_SPEAKING,
          vadEnabled: false,  // Disable VAD while speaking error message
          history: [...state.history, { from: currentState, to: STATES.AI_SPEAKING, event: type }]
        };
      }
      // From AI_SPEAKING or USER_SPEAKING, go directly to IDLE
      if (currentState === STATES.AI_SPEAKING || currentState === STATES.USER_SPEAKING) {
        return {
          ...state,
          currentState: STATES.IDLE,
          vadEnabled: true,
          history: [...state.history, { from: currentState, to: STATES.IDLE, event: type }]
        };
      }
      return state;

    default:
      return state;
  }
}

export const useTurnTakingMachine = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const send = useCallback((event, payload) => {
    dispatch({ type: event, payload });
  }, []);

  return {
    state: state.currentState,
    vadEnabled: state.vadEnabled,
    micEnabled: state.micEnabled,
    send,
    isIdle: state.currentState === STATES.IDLE,
    isUserSpeaking: state.currentState === STATES.USER_SPEAKING,
    isProcessing: state.currentState === STATES.PROCESSING,
    isAISpeaking: state.currentState === STATES.AI_SPEAKING,
    EVENTS,
    STATES
  };
};
